import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Divider,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as PlayIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pause as PauseIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { callAPI } from "../../../functions/api";

const WorkflowsOverview = ({ onCreateWorkflow, onOpenWorkflow, currentWorkflow }) => {
  const theme = useTheme();
  const [workflows, setWorkflows] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("updated");
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newWorkflowName, setNewWorkflowName] = useState("");
  const [activeTab, setActiveTab] = useState("workflows"); // New state for tab switching

  // Statistics for the overview cards
  const [stats, setStats] = useState({
    productionExecutions: 0,
    failedProductionExecutions: 0,
    failureRate: "0%",
    timeSaved: "--",
    runTimeAvg: "0s",
  });

  // Load workflows from API
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Mock workflow data that matches n8n style
      setWorkflows([
        {
          id: "wf-1",
          name: "My workflow 3",
          status: "inactive",
          lastUpdated: new Date(Date.now() - 21 * 60 * 60 * 1000), // 21 hours ago
          created: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000), // 14 July
          owner: "Personal",
          isActive: false,
        },
        {
          id: "wf-2",
          name: "My workflow 2",
          status: "inactive",
          lastUpdated: new Date(Date.now() - 23 * 60 * 60 * 1000), // 23 hours ago
          created: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000), // 14 July
          owner: "Personal",
          isActive: false,
        },
        {
          id: "wf-3",
          name: "My workflow",
          status: "inactive",
          lastUpdated: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000), // 6 days ago
          created: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000), // 9 July
          owner: "Personal",
          isActive: false,
        },
      ]);

      // Mock execution data matching the image
      setExecutions([
        {
          id: "exec-1",
          workflowId: "wf-2",
          workflowName: "My workflow 2",
          status: "Success",
          started: "Jul 14, 12:48:42",
          runTime: "22ms",
          execId: "1",
        },
      ]);

      setStats({
        productionExecutions: 1,
        failedProductionExecutions: 0,
        failureRate: "0%",
        timeSaved: "-- 0",
        runTimeAvg: "22ms",
      });
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort workflows
  const filteredWorkflows = useMemo(() => {
    let filtered = workflows.filter((workflow) => {
      const matchesSearch = workflow.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case "updated":
          return new Date(b.lastUpdated) - new Date(a.lastUpdated);
        case "name":
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });
  }, [workflows, searchQuery, sortBy]);

  const handleMenuOpen = (event, workflow) => {
    event.stopPropagation();
    setSelectedWorkflow(workflow);
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedWorkflow(null);
  };

  const handleCreateWorkflow = async () => {
    if (!newWorkflowName.trim()) return;

    try {
      const newWorkflow = {
        name: newWorkflowName,
        nodes: [],
        edges: [],
      };

      if (onCreateWorkflow) {
        await onCreateWorkflow(newWorkflow, true); // Pass true to switch to editor tab
      }

      setCreateDialogOpen(false);
      setNewWorkflowName("");
      loadData();
    } catch (error) {
      console.error("Failed to create workflow:", error);
    }
  };

  const handleDeleteWorkflow = async () => {
    if (!selectedWorkflow) return;

    try {
      await callAPI(`workflows/${selectedWorkflow.id}`, "DELETE");
      setDeleteDialogOpen(false);
      handleMenuClose();
      loadData();
    } catch (error) {
      console.error("Failed to delete workflow:", error);
    }
  };

  const formatDate = (date) => {
    if (!date) return "Never";
    const now = new Date();
    const diff = now - new Date(date);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (hours < 24) return `${hours} hours ago`;
    return `${days} days ago`;
  };

  const formatCreatedDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: "center", color: "#ccc" }}>
          Loading workflows...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#333333",
        minHeight: "100vh",
        color: "#fff",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <Box sx={{ p: 3, paddingTop: "40px" }}>
        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                backgroundColor: "#333333",
                border: "1px solid #444",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>
                Executions
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", fontSize: "12px", mb: 1 }}>
                Last 7 days
              </Typography>
              <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>
                {stats.productionExecutions}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                backgroundColor: "#333333",
                border: "1px solid #444",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>
                Failed executions
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", fontSize: "12px", mb: 1 }}>
                Last 7 days
              </Typography>
              <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>
                {stats.failedProductionExecutions}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                backgroundColor: "#333333",
                border: "1px solid #444",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>
                Failure rate
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", fontSize: "12px", mb: 1 }}>
                Last 7 days
              </Typography>
              <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>
                {stats.failureRate}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                backgroundColor: "#333333",
                border: "1px solid #444",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>
                Time saved
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", fontSize: "12px", mb: 1 }}>
                Last 7 days
              </Typography>
              <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>
                {stats.timeSaved}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                backgroundColor: "#333333",
                border: "1px solid #444",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>
                Run time (avg.)
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", fontSize: "12px", mb: 1 }}>
                Last 7 days
              </Typography>
              <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>
                {stats.runTimeAvg}
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: "1px solid #444", mb: 3 }}>
          <Box sx={{ display: "flex", gap: 4 }}>
            <Button
              onClick={() => setActiveTab("workflows")}
              sx={{
                color: activeTab === "workflows" ? "#FF6C58" : "#888",
                borderBottom: activeTab === "workflows" ? "2px solid #FF6C58" : "none",
                borderRadius: 0,
                textTransform: "none",
                fontWeight: 500,
                py: 2,
                "&:hover": { backgroundColor: "transparent", color: "#fff" },
              }}
            >
              Workflows
            </Button>
            <Button
              onClick={() => setActiveTab("executions")}
              sx={{
                color: activeTab === "executions" ? "#FF6C58" : "#888",
                borderBottom: activeTab === "executions" ? "2px solid #FF6C58" : "none",
                borderRadius: 0,
                textTransform: "none",
                fontWeight: 500,
                py: 2,
                "&:hover": { backgroundColor: "transparent", color: "#fff" },
              }}
            >
              Executions
            </Button>
          </Box>
        </Box>

        {/* Search and Controls - Only show for workflows tab */}
        {activeTab === "workflows" && (
          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{
                  backgroundColor: "#333333",
                  borderRadius: 1,
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": { borderColor: "#444" },
                    "&:hover fieldset": { borderColor: "#555" },
                    "&.Mui-focused fieldset": { borderColor: "#FF6C58" },
                  },
                  "& .MuiInputBase-input::placeholder": { color: "#aaa" },
                }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: "#aaa", mr: 1 }} />,
                }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{
                    backgroundColor: "#333333",
                    color: "#fff",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FF6C58" },
                    "& .MuiSvgIcon-root": { color: "#aaa" },
                  }}
                >
                  <MenuItem value="updated">Sort by last updated</MenuItem>
                  <MenuItem value="name">Sort by name</MenuItem>
                </Select>
              </FormControl>
              <IconButton sx={{ color: "#aaa" }}>
                <FilterListIcon />
              </IconButton>
              <IconButton sx={{ color: "#aaa" }}>
                <MoreVertIcon />
              </IconButton>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{
                backgroundColor: "#FF6C58",
                color: "#fff",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": { backgroundColor: "#FF5A47" },
              }}
            >
              Create Workflow
            </Button>
          </Box>
        )}

        {/* Tab Content */}
        {activeTab === "workflows" && (
          <>
            {/* Workflows List */}
            {filteredWorkflows.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8, color: "#ccc" }}>
                <Typography variant="h6" sx={{ mb: 2, color: "#fff" }}>
                  No workflows yet
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  Create your first workflow to get started
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDialogOpen(true)}
                  sx={{
                    backgroundColor: "#FF6C58",
                    "&:hover": { backgroundColor: "#FF5A47" },
                  }}
                >
                  Create Your First Workflow
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {filteredWorkflows.map((workflow) => (
                  <Card
                    key={workflow.id}
                    onClick={() => onOpenWorkflow?.(workflow)}
                    sx={{
                      backgroundColor: "#333333",
                      border: "1px solid #444",
                      borderRadius: 1,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "#3a3a3a",
                        borderColor: "#555",
                      },
                      ...(currentWorkflow?.id === workflow.id && {
                        backgroundColor: "#3a3a3a",
                        borderColor: "#FF6C58",
                      }),
                    }}
                  >
                    <CardContent sx={{ py: 2, px: 3, "&:last-child": { pb: 2 } }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        {/* Left section - Name and Owner */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 3, flex: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{ color: "#fff", fontWeight: 500, minWidth: 200 }}
                          >
                            {workflow.name}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 16, color: "#aaa" }} />
                            <Typography variant="body2" sx={{ color: "#aaa", minWidth: 80 }}>
                              {workflow.owner}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Center section - Status */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 120 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: workflow.isActive ? "#4CAF50" : "#666",
                              }}
                            />
                            <Typography variant="body2" sx={{ color: "#aaa" }}>
                              {workflow.isActive ? "Active" : "Inactive"}
                            </Typography>
                          </Box>
                          <Switch
                            checked={workflow.isActive}
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": { color: "#FF6C58" },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: "#FF6C58",
                              },
                            }}
                          />
                        </Box>

                        {/* Right section - Timestamps and Actions */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <Typography
                            variant="body2"
                            sx={{ color: "#aaa", minWidth: 100, textAlign: "right" }}
                          >
                            {formatDate(workflow.lastUpdated)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#aaa", minWidth: 80, textAlign: "right" }}
                          >
                            {formatCreatedDate(workflow.created)}
                          </Typography>

                          {/* Action buttons */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {/* Play button - Execute workflow */}
                            <IconButton
                              size="small"
                              sx={{ color: "#aaa", "&:hover": { color: "#4CAF50" } }}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Execute workflow functionality here
                                console.log("Execute workflow:", workflow.id);
                              }}
                              title="Execute workflow"
                            >
                              <PlayIcon sx={{ fontSize: 18 }} />
                            </IconButton>

                            {/* Copy/Duplicate button */}
                            <IconButton
                              size="small"
                              sx={{ color: "#aaa", "&:hover": { color: "#FF6C58" } }}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Duplicate workflow
                                console.log("Duplicate workflow:", workflow.id);
                              }}
                              title="Duplicate workflow"
                            >
                              <DuplicateIcon sx={{ fontSize: 18 }} />
                            </IconButton>

                            {/* Open button - Open workflow in editor */}
                            <IconButton
                              size="small"
                              sx={{ color: "#aaa", "&:hover": { color: "#2196F3" } }}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Open workflow in editor and switch to editor tab
                                if (onOpenWorkflow) {
                                  onOpenWorkflow(workflow, true); // Pass true to indicate we want to switch to editor
                                }
                              }}
                              title="Open in editor"
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>

                            {/* More options menu */}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMenuOpen(e, workflow);
                              }}
                              sx={{ color: "#aaa", "&:hover": { color: "#fff" } }}
                              title="More options"
                            >
                              <MoreVertIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {/* Pagination */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 3,
                color: "#888",
              }}
            >
              <Typography variant="body2">Total {filteredWorkflows.length}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2">1</Typography>
                <Typography variant="body2">50/page</Typography>
              </Box>
            </Box>
          </>
        )}

        {/* Executions Tab */}
        {activeTab === "executions" && (
          <>
            {/* Auto refresh toggle */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: "#FF6C58",
                    borderRadius: "2px",
                  }}
                />
                <Typography variant="body2" sx={{ color: "#fff" }}>
                  Auto refresh
                </Typography>
              </Box>
            </Box>

            {/* Executions Table */}
            <TableContainer
              component={Paper}
              sx={{
                backgroundColor: "#333333",
                border: "1px solid #444",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ "& .MuiTableCell-head": { borderBottom: "1px solid #444" } }}>
                    <TableCell sx={{ color: "#888", fontWeight: 500, pl: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <input type="checkbox" style={{ margin: 0 }} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#888", fontWeight: 500 }}>Workflow</TableCell>
                    <TableCell sx={{ color: "#888", fontWeight: 500 }}>Status</TableCell>
                    <TableCell sx={{ color: "#888", fontWeight: 500 }}>Started</TableCell>
                    <TableCell sx={{ color: "#888", fontWeight: 500 }}>Run Time</TableCell>
                    <TableCell sx={{ color: "#888", fontWeight: 500 }}>Exec. ID</TableCell>
                    <TableCell sx={{ color: "#888", fontWeight: 500, pr: 3 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {executions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: "center", py: 4, color: "#888" }}>
                        <Typography>No more executions to fetch</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    executions.map((execution) => (
                      <TableRow
                        key={execution.id}
                        sx={{
                          "&:hover": { backgroundColor: "#3a3a3a" },
                          "& .MuiTableCell-body": { borderBottom: "1px solid #444", color: "#fff" },
                        }}
                      >
                        <TableCell sx={{ pl: 3 }}>
                          <input type="checkbox" style={{ margin: 0 }} />
                        </TableCell>
                        <TableCell>{execution.workflowName}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {execution.status === "Success" ? (
                              <CheckCircleIcon sx={{ fontSize: 16, color: "#4CAF50" }} />
                            ) : (
                              <ErrorIcon sx={{ fontSize: 16, color: "#f44336" }} />
                            )}
                            <Typography
                              variant="body2"
                              sx={{ color: execution.status === "Success" ? "#4CAF50" : "#f44336" }}
                            >
                              {execution.status}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{execution.started}</TableCell>
                        <TableCell>{execution.runTime}</TableCell>
                        <TableCell>{execution.execId}</TableCell>
                        <TableCell sx={{ pr: 3 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <IconButton size="small" sx={{ color: "#888" }}>
                              <ErrorIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton size="small" sx={{ color: "#888" }}>
                              <MoreVertIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {executions.length === 0 && (
              <Box sx={{ textAlign: "center", mt: 4, color: "#888" }}>
                <Typography>No more executions to fetch</Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: "#333333",
            border: "1px solid #444",
            color: "#fff",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (onOpenWorkflow) {
              onOpenWorkflow(selectedWorkflow, true); // Pass true to switch to editor tab
            }
            handleMenuClose();
          }}
          sx={{ "&:hover": { backgroundColor: "#3a3a3a" } }}
        >
          <EditIcon sx={{ mr: 1 }} />
          Open
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ "&:hover": { backgroundColor: "#3a3a3a" } }}>
          <DuplicateIcon sx={{ mr: 1 }} />
          Duplicate
        </MenuItem>
        <Divider sx={{ borderColor: "#444" }} />
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true);
            handleMenuClose();
          }}
          sx={{ color: "#f44336", "&:hover": { backgroundColor: "#3a3a3a" } }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create Workflow Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#333333",
            border: "1px solid #444",
            color: "#fff",
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff" }}>Create New Workflow</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Workflow Name"
            fullWidth
            variant="outlined"
            value={newWorkflowName}
            onChange={(e) => setNewWorkflowName(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                "& fieldset": { borderColor: "#444" },
                "&:hover fieldset": { borderColor: "#555" },
                "&.Mui-focused fieldset": { borderColor: "#FF6C58" },
              },
              "& .MuiInputLabel-root": { color: "#888" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#FF6C58" },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} sx={{ color: "#888" }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateWorkflow}
            variant="contained"
            disabled={!newWorkflowName.trim()}
            sx={{
              backgroundColor: "#FF6C58",
              "&:hover": { backgroundColor: "#FF5A47" },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#333333",
            border: "1px solid #444",
            color: "#fff",
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff" }}>Delete Workflow</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#fff" }}>
            Are you sure you want to delete "{selectedWorkflow?.name}"? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: "#888" }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteWorkflow} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkflowsOverview;
